import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import "./Products.css";
import ProductCard from "./ProductCard";
import Cart, { generateCartItemsFrom } from "./Cart";
import { flexbox } from "@mui/system";

const Products = () => {
  const [product, setProduct] = useState([]);
  const [searching, setSearching] = useState(true);
  const [debouncing, setDebounching] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState([]);
  const token = localStorage.getItem("token");
  const [allProducts,setAllProducts] = useState([])

  useEffect(() => {
    const fetchAllProducts = async () => {
      const response = await performAPICall();
      setAllProducts(response)
      setProduct(response)
    }
    fetchAllProducts()
  }, []);

  useEffect(() => {
    fetchCart(token)
      .then((cartData) => generateCartItemsFrom(cartData, allProducts))
      .then((cartItems) => setItems(cartItems));
  }, [allProducts]);

  const isItemInCart = (items, productId) => {
    return items.some((item) => item.productId === productId);
  };

  const addToCart = async (
    token,
    items,
    products,
    productId,
    qty,
    options = { preventDuplicate: false }
  ) => {
    if (!token) {
      enqueueSnackbar("Please log in to add items to the cart", {
        variant: "warning",
      });
      return;
    }

    if (options.preventDuplicate && isItemInCart(items, productId)) {
      enqueueSnackbar(
        "Item already in cart. Use the cart sidebar to update quantity or remove item",
        { variant: "warning" }
      );
      return;
    }

    try {
      const response = await axios.post(
        `${config.endpoint}/cart`,
        { productId, qty },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const cartItems = generateCartItemsFrom(response.data, products);
      setItems(cartItems);
    } catch (error) {
      if (error.response) {
        enqueueSnackbar(error.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch the products. Check if the backend is running",
          { variant: "error" }
        );
      }
    }
  };

  const fetchCart = async (token) => {
    if (!token) return;

    try {
      const response = await axios.get(`${config.endpoint}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(response.data);
      return response.data;
    } catch (e) {
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch cart details. Check that the backend is running, reachable and returns valid JSON.",
          { variant: "error" }
        );
      }
      return null;
    }
  };

  const performAPICall = async () => {
    setSearching(true);
    try {
      const response = await axios.get(`${config.endpoint}/products`);
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      console.error("API call failed:", error);
      return [];
    } finally {
      setSearching(false);
    }
  };

  const performSearch = async (text) => {
    setSearching(true);
    setProduct([]);
    try {
      if (text === "") {
        return performAPICall();
      } else {
        const response = await axios.get(
          `${config.endpoint}/products/search?value=${text}`
        );
        return Array.isArray(response.data) ? response.data : [];
      }
    } catch (error) {
      return [];
    } finally {
      setSearching(false);
    }
  };

  const debounceSearch = (event, debounceTimeout) => {
    if (debouncing) clearTimeout(debouncing);
    const timer = setTimeout(async () => {
      const searchProduct = await performSearch(event.target.value);
      setProduct(searchProduct);
    }, debounceTimeout);
    setDebounching(timer);
  };

  const displayProducts = () => (
    <Grid container>
      {/* Hero Section */}
      <Grid container>
        <Grid item className="product">
          <Box className="hero">
            <p className="hero-heading">
              India’s <span className="hero-highlight">FASTEST DELIVERY</span> to your door step
            </p>
          </Box>
        </Grid>
      </Grid>

      {/* Loading, No Results or Product Grid */}
      {searching ? (
        <div className="loading">
          <CircularProgress />
          <p>Loading Products</p>
        </div>
      ) : product.length === 0 ? (
        <div className="no-product-found">
          <SentimentDissatisfied />
          <p>No products found</p>
        </div>
      ) : (
        <Grid container>
          {product.map((element, index) => (
            <Grid key={index} item sm={6} md={3} sx={{ padding: 1 }}>
              <ProductCard
                product={element}
                handleAddToCart={async () =>
                  await addToCart(
                    localStorage.getItem("token"),
                    items,
                    product,
                    element._id,
                    1,
                    { preventDuplicate: true }
                  )
                }
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );

  return (
    <div>
      <Header>
        <TextField fullWidth
          className="search-desktop"
          size="small"
          sx={{ width: "50%", display: flexbox }}   
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search color="primary" />
              </InputAdornment>
            ),
          }}
          placeholder="Search for items/categories"
          name="search"
          onChange={(e) => debounceSearch(e, 500)}
        />
      </Header>

      <TextField
        className="search-mobile"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
        placeholder="Search for items/categories"
        name="search"
        onChange={(e) => debounceSearch(e, 500)}
      />

      <Grid container>
        {token ? (
          <Grid container>
            <Grid item md={9}>
              {displayProducts()}
            </Grid>
            <Grid item xs={12} md={3}>
              <Cart
                items={items}
                products={product}
                handleQuantity={async (productId, qty) =>
                  await addToCart(token, items, product, productId, qty)
                }
              />
            </Grid>
          </Grid>
        ) : (
          displayProducts()
        )}
      </Grid>

      <Footer />
    </div>
  );
};

export default Products;
